import { useCallback } from 'react';
import { useStore } from 'zustand';
import { SubcontractRow } from './SubcontractRow';
import { SubcontractCloseout } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import { useGetDefaultSubcontractExpenseTypes } from '../../hooks/useDefaultCloseoutHooks';
import { useUnityBuildStore } from '../../stores/store';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  useGetSubcontractCloseouts,
  useCreateSubcontractCloseoutMutation,
  useCreateSubcontractChangeOrderCloseoutMutation,
  useGetSubcontractChangeOrderCloseouts,
} from '../../hooks/useCloseoutHooks';
import { SubcontractChangeOrderCloseout } from '../../api/protosCompiled/changeOrderCloseout/changeOrderCloseout_pb';
import { useLockBid } from '../../utils/useLockBid';

export const rowStyle = { width: '85px' };

export const SubcontractHeader = () => {
  const theme = useTheme();

  // Common styles matching TotalPricingRow and TotalPricingHeader
  const headerCellStyle = {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };
  const { lockBid } = useLockBid();
  // Zustand state
  const { selectedEstimate, selectedChangeOrder } = useStore(useUnityBuildStore);

  const { data: closeoutDefaults } = useGetDefaultSubcontractExpenseTypes();

  const { data: estimateCloseout, refetch: refetchEstimateCloseout } = useGetSubcontractCloseouts();

  const { data: changeOrderCloseout, refetch: refetchChangeOrderCloseout } =
    useGetSubcontractChangeOrderCloseouts();

  const { mutateAsync: createEstimateCloseout } = useCreateSubcontractCloseoutMutation();

  const { mutateAsync: createChangeOrderCloseout } =
    useCreateSubcontractChangeOrderCloseoutMutation();

  const refetch = selectedEstimate?.estimateid
    ? refetchEstimateCloseout
    : refetchChangeOrderCloseout;

  const createCloseout = selectedEstimate?.estimateid
    ? createEstimateCloseout
    : createChangeOrderCloseout;

  const closeout = selectedEstimate?.estimateid ? estimateCloseout : changeOrderCloseout;

  const newCloseoutType = selectedEstimate?.estimateid
    ? new SubcontractCloseout()
    : new SubcontractChangeOrderCloseout();

  const takeoffId = selectedEstimate?.estimateid
    ? selectedEstimate?.estimateid
    : (selectedChangeOrder?.changeorderid ?? '');

  const handleAddDefaultType = useCallback(
    (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value;
      const defaultCloseoutType = closeoutDefaults?.find(
        closeoutDefault => closeoutDefault.name === newValue
      );
      if (defaultCloseoutType) {
        newCloseoutType.setCloseoutid(defaultCloseoutType.id);
        newCloseoutType.setEstimateid(takeoffId);
        newCloseoutType.setCloseoutdefaultsubcontractid(defaultCloseoutType.id);
        newCloseoutType.setQuotedcost(defaultCloseoutType.quotedcost);
        newCloseoutType.setAdjustedpercent(defaultCloseoutType.adjustedpercent);

        createCloseout(newCloseoutType, {
          onSuccess: () => {
            refetch();
          },
        }).catch(err => console.error('Error creating subcontract:', err));
      }
    },
    [closeoutDefaults, selectedEstimate, selectedChangeOrder, createCloseout, refetch]
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
      role="region"
      aria-label="Subcontract Management"
    >
      <FormControl
        fullWidth
        variant="filled"
        sx={{
          mb: 1,
          '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiFilledInput-root': {
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.common.black, 0.02),
            },
            '&.Mui-focused': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.08)
                  : alpha(theme.palette.common.black, 0.05),
            },
          },
          '& .MuiSelect-select': {
            color: theme.palette.text.primary,
          },
          '& .MuiSelect-icon': {
            color: theme.palette.text.secondary,
          },
        }}
      >
        <Tooltip title="Choose a subcontract type to add to the table" arrow>
          <InputLabel id="subcontract-select-label">Select Subcontract Type</InputLabel>
        </Tooltip>
        <Select
          labelId="subcontract-select-label"
          id="subcontract-select"
          label="Select Subcontract Type"
          value={'Select Subcontract Type'}
          onChange={handleAddDefaultType}
          aria-label="Select a subcontract type to add"
          disabled={lockBid()}
        >
          <MenuItem value="" disabled selected hidden>
            Select subcontract type
          </MenuItem>
          {closeoutDefaults &&
            closeoutDefaults.map((closeoutItem, index) => (
              <MenuItem
                value={closeoutItem.name}
                key={index}
                aria-label={`Add ${closeoutItem.name} subcontract type`}
                sx={{
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {closeoutItem.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Box sx={{ flex: 1, overflow: 'hidden' }} role="region" aria-label="Subcontract Table">
        <TableContainer
          component={Paper}
          sx={{
            height: '100%',
            overflowY: 'auto',
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
            '& .MuiPaper-root': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Table
            stickyHeader
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderColor: theme.palette.divider,
              },
            }}
            aria-label="Subcontracts"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Name or description of the subcontract" arrow>
                    <span>Description</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Quoted cost for the subcontract" arrow>
                    <span>Quoted Cost</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Percentage adjustment applied to the quoted cost" arrow>
                    <span>% Adjustment</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total cost after adjustments" arrow>
                    <span>Total Cost</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Actions available for this entry" arrow>
                    <span>Actions</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {closeout?.closeoutsList.map((closeoutItem, index) => (
                <SubcontractRow key={index} closeout={closeoutItem} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
